import React, { useState, useEffect } from 'react'
import { Badge, Button, Col, Form, Modal, Row, Spinner, Dropdown } from 'react-bootstrap'
import Header from '../../layouts/Header'
import { toast } from 'react-toastify'
import ReusableTable from '../../components/ReusableTable'
import { TrashFill } from 'react-bootstrap-icons'
import { useSelector } from 'react-redux'
import axiosInstance from '../../api/axios'

const VersionControl = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : ''
    const [skin, setSkin] = useState(currentSkin)
    const { access } = useSelector((state) => state.login)
    const { email } = useSelector((state) => state.login)
    const [systemAccess, setSystemAccess] = useState([])
    
    useEffect(() => {
        if (Array.isArray(access)) {
            const filteredAccess = access?.filter((perm) =>
                perm?.startsWith("AppVersion:")
            )
            setSystemAccess(filteredAccess)
        } 
    }, [access])
    
    const accessToken = localStorage.getItem('authToken')
    const [openAddModal, setOpenAddModal] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [versionList, setVersionList] = useState([])
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const [VersionId, setVersionId] = useState("")
    const [filteredData, setFilteredData] = useState([])
    const [searchText, setSearchText] = useState("")
    const [IsDeleteBtn, setIsDeleteBtn] = useState(false)
    const [errors, setErrors] = useState({});

    const [selectedOsType, setSelectedOsType] = useState("");
    
    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        rowsPerPage: 10,
        totalRecords: 0,
        totalPages: 1
    })
    
    const [isLoadingTable, setIsLoadingTable] = useState(false)
    const [debounceTimer, setDebounceTimer] = useState(null)
    
    const [versionForm, setVersionForm] = useState({
        version_number: '',
        os_type: '',
        is_current_version: ''
    })

    const columns = [
        { 
            header: "Sr.No", 
            accessor: "serialNumber",
            render: (value, row, index) => {
                // Calculate serial number based on current page
                const serialNumber = ((pagination.page - 1) * pagination.rowsPerPage) + index + 1
                return serialNumber
            }
        },
        {
            header: "Created On",
            accessor: "createdAt",
            render: (value, row) => (
                <div>
                    <span className='text-secondary'>
                        {new Date(row?.createdAt).toLocaleString('en-US', {
                            month: '2-digit',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                            hour12: true
                        })}
                    </span>
                </div>
            ),
        },
        {
            header: "Version Number",
            accessor: "version_number",
            render: (value, row) => (
                <div>
                    <h6 className='text-primary'>{row?.version_number}</h6>
                </div>
            ),
        },
        { 
            header: "OS Type", 
            accessor: "os_type",
            render: (value, row) => (
                <div>
                    <span className='w-75 text-center'
                        style={{
                            color: row?.os_type === 'IOS' ? '#8E44AD' : '#3498DB',
                            fontWeight: 500,
                            padding: '3px 8px',
                            borderRadius: '12px',
                            backgroundColor: row?.os_type === 'IOS' ? 'rgba(142, 68, 173, 0.1)' : 'rgba(52, 152, 219, 0.1)',
                            fontSize: '0.80rem',
                            display: 'inline-block'
                        }}
                    >
                        {row?.os_type}
                    </span>
                </div>
            )
        },
        {
            header: "Status",
            accessor: "is_current_version",
            render: (value, row) => (
                <div className="d-flex justify-content-center">
                    {row.is_current_version ? (
                        <Badge bg="success" className='fs-7 w-100'>Active</Badge>
                    ) : (
                        <Badge bg="danger" className='fs-7 w-100'>Inactive</Badge>
                    )}
                </div>
            ),
        },
        ...(
            (systemAccess?.includes("AppVersion:update") || systemAccess?.includes("AppVersion:delete") ||  email === "contact@xpansecafe.com")
                ? [
                { 
                    header: <div className="text-center">Actions</div>, 
                    accessor: null, 
                    render: (_, row) => (
                        <div className="d-flex justify-content-center align-items-center">
                            {(systemAccess?.includes("AppVersion:delete") || email === "contact@xpansecafe.com") && (
                                <Button
                                    variant="link"
                                    className="text-danger p-0 mx-2"
                                    onClick={() => handleDeleteModal(row)}
                                    title="Delete"
                                >
                                    DELETE <TrashFill size={16} />
                                </Button>
                            )}
                        </div>
                    )
                },
            ] : [] 
        )
    ]

    // Get all versions with pagination
    const getAllVersion = async (page = 1, limit = pagination.rowsPerPage, search = '', osType) => {
        setIsLoadingTable(true)
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`
            }
            
            // Prepare query parameters
            let params = {
                page: page,
                limit: limit,
                ...(search && { search: search }),
                ...(osType && { os_type: osType })
            }
            
            const response = await axiosInstance.get("version", { 
                headers,
                params 
            })
            
            // Set the data
            setVersionList(response.data?.data?.versions || [])
            setFilteredData(response.data?.data?.versions || [])
            
            // Update pagination info from server response
            if (response.data?.data?.pagination) {
                setPagination(prev => ({
                    ...prev,
                    page: response.data.data.pagination.page,
                    totalRecords: response.data.data.pagination.total,
                    totalPages: response.data.data.pagination.pages
                }))
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                })
            }
        } finally {
            setIsLoadingTable(false)
        }
    }

    const validateForm = () => {
        const newErrors = {}
        if (!versionForm.os_type) {
            newErrors.os_type = "OS Type is required"
        }
        if (versionForm.is_current_version === "" || versionForm.is_current_version === null || versionForm.is_current_version === undefined) {
            newErrors.is_current_version = "Current Version Selection is required"
        }
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleOpenAddModal = (e) => {
        setOpenAddModal(true)
    }

    const handleCloseAddModal = () => {
        setOpenAddModal(false)
        setVersionForm({
            version_number: '',
            os_type: '',
            is_current_version: ''
        })
    }

    const handleChange = (e) => {
        const { name, value } = e.target
        const newValue = value === 'true' ? true : value === 'false' ? false : value
        setVersionForm({
            ...versionForm,
            [name]: newValue
        })
    }

    const addVersion = async (e) => {
        e.preventDefault()
        if (validateForm()) {
            setIsLoading(true)
            try {
                const headers = {
                    Authorization: `Bearer ${accessToken}`,
                }
                const response = await axiosInstance.post("version", versionForm, { headers })
                if (response?.data?.status === true) {
                    toast.success(response?.data?.data?.message, {
                        position: "top-right",
                        autoClose: 3000,
                        theme: "colored",
                    })
                    // Refresh the table with current pagination
                    getAllVersion(pagination.page, pagination.rowsPerPage, searchText, selectedOsType)
                    setVersionForm({
                        version_number: "",
                        os_type: "",
                        is_current_version: ""
                    })
                    setOpenAddModal(false)
                }
            } catch (error) {
                if (error?.response) {
                    let errorMessage = ''
                    
                    if ( error.response?.status === 400 ) {
                        errorMessage = error?.response?.data?.data?.message || error?.response?.data?.error
                    }
                    else if ( error.response?.status === 404 ) {
                        errorMessage = error?.response?.data?.data?.message
                    } else if ( error.response?.status === 406 ) {
                        errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error
                    } else if ( error.response?.status === 412 ) {
                        errorMessage = error?.response?.data?.data?.message
                    } else if (error.response?.status === 500) {
                        errorMessage = error?.response?.data?.error
                    }

                    toast.error(errorMessage, {
                        position: "top-right",
                        autoClose: 3000,
                        theme: "light",
                    })
                }
            } finally {
                setIsLoading(false)
            }
        }
    }

    const handleDeleteModal = async (data) => {
        setOpenDeleteModal(true)
        setVersionId(data?._id)
    }

    const handleDeleteVersion = async (e) => {
        e.preventDefault()
        setIsDeleteBtn(true)
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            }
            const data = {
                _id: VersionId
            }

            const response = await axiosInstance.delete("version", { headers, data })
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                })
                // Refresh the table with current pagination
                getAllVersion(pagination.page, pagination.rowsPerPage, searchText, selectedOsType)
                setOpenDeleteModal(false)
            }
        } catch (error) {
            if (error.response.status === 403) {
                toast.error(error?.response?.data?.message, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                })
            }

            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                })
            }
        } finally {
            setIsDeleteBtn(false)
        }
    }

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false)
    }

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, page }))
        getAllVersion(page, pagination.rowsPerPage, searchText, selectedOsType)
    }
  
    const handleRowsPerPageChange = (rowsPerPage) => {
        const newPagination = {
            page: 1,
            rowsPerPage: rowsPerPage,
            totalRecords: pagination.totalRecords,
            totalPages: Math.ceil(pagination.totalRecords / rowsPerPage)
        }
        setPagination(newPagination)
        getAllVersion(1, rowsPerPage, searchText, selectedOsType)
    }

    const handleSearchChange = (e) => {
        const value = e.target.value
        setSearchText(value)
        
        // Clear previous timer
        if (debounceTimer) {
            clearTimeout(debounceTimer)
        }
        
        // Set new timer for debouncing
        const timer = setTimeout(() => {
            getAllVersion(1, pagination.rowsPerPage, value, selectedOsType)
        }, 500) // 500ms delay
        
        setDebounceTimer(timer)
    }

    const handleOsTypeChange = (e) => {
        setSelectedOsType(e.target.value);
        let osType = e.target.value;
        getAllVersion(pagination?.page, pagination?.rowsPerPage, searchText, osType)
    }

    // Records per page options
    const recordsPerPageOptions = [5, 10, 25, 50, 100]

    useEffect(() => {
        getAllVersion(pagination?.page, pagination?.rowsPerPage)
        
        // Cleanup debounce timer on unmount
        return () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer)
            }
        }
    }, []);

    // Records per page selector component
    const RecordsPerPageSelector = () => (
        <div className="d-flex align-items-center">
            <span className="me-2">Show:</span>
            <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                    {pagination.rowsPerPage}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {recordsPerPageOptions.map(option => (
                        <Dropdown.Item 
                            key={option} 
                            onClick={() => handleRowsPerPageChange(option)}
                            active={pagination.rowsPerPage === option}
                        >
                            {option}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
            <span className="ms-2">entries</span>
        </div>
    )

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className="main main-app p-3 p-lg-4">
                <div
                    className="container shadow-sm"
                    style={{ backgroundColor: "white" }}
                >
                    <Row className="pt-4">
                        <Col>
                            <h5 className="fw-semibold">APP VERSION CONTROL</h5>
                        </Col>
                        <hr />
                    </Row>

                    <Row className='mb-3'>
                        <Col lg="3">
                            <div className="input-group w-100">
                                <input
                                    type="text"
                                    className="form-control"
                                    value={searchText}
                                    onChange={handleSearchChange}
                                    placeholder="Search for versions..."
                                />
                                <button className="btn btn-primary" type="button">
                                    <i className="ri-search-line"></i>
                                </button>
                            </div>
                        </Col>
                        <Col lg="7">
                            <Form.Select className='w-25' name="osType" value={selectedOsType} onChange={handleOsTypeChange}>Select Os Type
                                <option value="">Select Os Type</option>
                                <option value="ANDROID">ANDROID</option>
                                <option value="IOS">IOS</option>
                            </Form.Select>
                        </Col>
                        
                        <Col lg="2" className='text-end d-flex align-items-center justify-content-end'>
                            
                            {(systemAccess?.includes("AppVersion:add") || email === "contact@xpansecafe.com") && (
                                <Button
                                    variant="primary"
                                    onClick={handleOpenAddModal}
                                    className="fw-semibold"
                                >
                                <i className="ri-add-line" style={{ marginRight: '5px' }}></i>
                                    Add New Version
                                </Button>
                            )}
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <div className="me-3">
                                <RecordsPerPageSelector />
                            </div>
                        </Col>
                    </Row>

                    <Row className='mt-3'>
                        <Col lg="12">
                            {isLoadingTable ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2">Loading versions...</p>
                                </div>
                            ) : (
                                (systemAccess?.includes("AppVersion:get") || email === "contact@xpansecafe.com") && (
                                    <ReusableTable
                                        columns={columns}
                                        data={filteredData}
                                        currentPage={pagination.page}
                                        recordsPerPage={pagination.rowsPerPage}
                                        onPageChange={handlePageChange}
                                        totalPages={pagination.totalPages}
                                        onRecordsPerPageChange={handleRowsPerPageChange}
                                        totalRecords={pagination.totalRecords}
                                        rowKey="_id"
                                    />
                                )
                            )}
                        </Col>
                    </Row>

                    {/* Add Version Modal */}
                    {openAddModal && (
                        <Modal show={openAddModal} onHide={handleCloseAddModal}>
                            <Modal.Header closeButton>
                                <Modal.Title className="fs-6 fw-semibold pt-1">
                                    ADD APP VERSION
                                </Modal.Title>
                            </Modal.Header>
                            <Form onSubmit={addVersion}>
                                <Modal.Body>
                                    <div className="mb-3">
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">
                                                Version Number <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Version Number"
                                                required
                                                name="version_number"
                                                value={versionForm.version_number}
                                                onChange={handleChange}
                                                onInput={(e) => {
                                                    e.target.value = e.target.value.replace(/[^0-9.]/g, '')
                                                }}
                                                pattern="^\d+(\.\d+){0,2}$"
                                                title="Version number must be integer and must be in the format x, x.x, or x.x.x where x is a number"
                                            />
                                        </Form.Group>
                                        
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">
                                                OS Type <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select id="os_type" name="os_type" value={versionForm.os_type} onChange={handleChange}>
                                                <option value="" disabled>Select OS Type</option>
                                                <option value="ANDROID">Android</option>
                                                <option value="IOS">IOS</option>
                                            </Form.Select>
                                            {errors.os_type && <div className='text-danger' style={{ fontSize: '12px' }}>{errors.os_type}</div>}
                                        </Form.Group>
                                        
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">
                                                Status <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select id="is_current_version" name="is_current_version" value={versionForm.is_current_version} onChange={handleChange}>
                                                <option value="" disabled>Select</option>
                                                <option value="true">ACTIVE</option>
                                                <option value="false">INACTIVE</option>
                                            </Form.Select>
                                            {errors.is_current_version && <div className='text-danger' style={{ fontSize: '12px' }}>{errors.is_current_version}</div>}
                                        </Form.Group>
                                    </div>
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="secondary" onClick={handleCloseAddModal}>
                                        Cancel
                                    </Button>
                                    <Button variant='primary' type='submit' disabled={isLoading}>
                                        { isLoading ? (
                                            <>
                                                <Spinner size='sm' className="me-2" />
                                                Adding...
                                            </>
                                        ) : 'ADD VERSION' }
                                    </Button>
                                </Modal.Footer>
                            </Form>
                        </Modal>
                    )}

                    {/* Delete Version Modal */}
                    {openDeleteModal && (
                        <Modal show={openDeleteModal} onHide={handleCloseDeleteModal} centered>
                            <Modal.Header closeButton>
                                <Modal.Title className='text-danger'>Confirm Delete</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                Are you sure you want to delete this Version? This action cannot be undone.
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleCloseDeleteModal}>
                                    Cancel
                                </Button>
                                <Button variant="danger" onClick={handleDeleteVersion} disabled={IsDeleteBtn}>
                                    { IsDeleteBtn ? (
                                        <>
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                                className="me-2"
                                            />
                                            Deleting...
                                        </>
                                    ) : "Delete Version" }
                                </Button>
                            </Modal.Footer>
                        </Modal>
                    )}
                </div>
            </div>
        </React.Fragment>
    )
}

export default VersionControl